import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  MeasurementsConfig,
  MeasurementTypes,
} from "../../../../@types/Measurements";
import { Notification } from "../../../../@types/Notification";
import { ISectionProps } from "../../../../@types/Patient";
import { ChevronDown } from "../../../../assets/icons/ChevronDown";
import { ChevronUp } from "../../../../assets/icons/ChevronUp";
import { CustomIconButton } from "../../../../components/CustomIconButton";
import { DualAxisChartCard } from "../../../../components/DualAxisChartPoc/DualAxisChartCard";
import { DualAxisChartModal } from "../../../../components/DualAxisChartPoc/DualAxisChartModal";
import { spirometryMeasurementsValues } from "../../../../constants/measurements";
import { DEFAULT_VIEW_TYPE } from "../../../../constants/viewTypes";
import { useMeasurementConfigs } from "../../../../hooks/queries/measurement-configs";
import { usePatientWards } from "../../../../hooks/queries/patients";
import useIsMobile from "../../../../hooks/useIsMobile";
import { useModal } from "../../../../hooks/useModal";
import { useToast } from "../../../../hooks/useToast";
import { useUserPreferences } from "../../../../hooks/useUserPreferences";
import { PageTitle } from "../../../../layout/components/PageTitle";
import { isUat } from "../../../../utils/environment";
import { mixpanelActions } from "../../../../utils/mixpanel";
import {
  isWardFeatureEnabledForPatient,
  resolvePatientWardIds,
} from "../../../../utils/wardConfig";
import {
  ChartsContainer,
  MobilePatientPageTitleContainer,
} from "../../PatientPage.styles";
import { AddMeasurementModal } from "../AddMeasurementModal";
import { EcgCard } from "../EcgCard";
import { MeasurementCard } from "../MeasurementCard";
import {
  AddMeasurementButton,
  NewDataCOntainer,
  TitleContainer,
} from "./MeasurementSection.styles";

interface IMeasurementSectionProps extends ISectionProps {
  configs?: MeasurementsConfig[];
}

export function MeasurementSection({
  patient,
  updateColapsedSections,
  collapsedSection,
  openFev1Graph,
  onFev1GraphOpen,
  configs: propConfigs,
}: IMeasurementSectionProps) {
  const { t } = useTranslation();

  // All hooks must be called unconditionally at the top
  const { portalPreferences, preferences, changeUserPreferences } =
    useUserPreferences();
  const { isMobile } = useIsMobile();
  const { openModal, closeModal } = useModal();
  const { showToast } = useToast();
  const [showNewData, setShowNewData] = useState<boolean>(false);

  // EN-4960: the patient's full ward set drives ward feature flags (unioned across wards).
  const { patientWards } = usePatientWards(patient?.id);

  // The ECG card is shown when ANY of the patient's wards has the AliveCor ECG integration enabled.
  const isEcgEnabled = useMemo(() => {
    const patientWardIds = resolvePatientWardIds(
      patientWards?.wardIds,
      patient?.hospitalId
    );

    return isWardFeatureEnabledForPatient(
      patientWardIds,
      "enableAliveCorEcgIntegration"
    );
  }, [patientWards, patient?.hospitalId]);

  // Use props if provided, otherwise fetch via hook (for backwards compatibility)
  const { configs: hookConfigs } = useMeasurementConfigs({
    patientId: propConfigs ? undefined : patient?.id,
    section: "measurements",
  });

  // fitbit_sleep is shipped by the backend measurement configs (gated on the
  // Fitbit/Google Health integration), alongside fitbit_activity and daily_steps.
  const configs = propConfigs ?? hookConfigs;

  // Initialize preferences if not set
  if (!preferences) {
    changeUserPreferences({
      hideFvcRejected: false,
      hideFev1Rejected: false,
      hideFvcUsable: false,
      hideFev1Usable: false,
    });
  }

  const handleOnModalClose = (notification?: Notification) => {
    if (notification !== undefined) {
      showToast(notification.message, notification.type);

      setTimeout(() => {
        window.location.reload();
      }, 500);
    }
    closeModal();
  };

  const handleAddMeasurement = () => {
    mixpanelActions.track(`User action: Add Measurement Button`);
    openModal(
      <AddMeasurementModal
        section="measurement"
        patientId={patient?.id.toString()}
        onClose={handleOnModalClose}
      />,
      {
        width: isMobile ? "98vw" : "620px",
        height: isMobile ? undefined : "auto",
        borderRadius: isMobile ? "8px" : undefined,
        showCloseButton: true,
      }
    );
  };

  const checkNewData = (mostRecentDate: dayjs.Dayjs) => {
    if (showNewData === false) {
      const collapsedDate = dayjs(collapsedSection.collapsedDate);
      if (mostRecentDate > collapsedDate) {
        setShowNewData(true);
      }
    }
  };

  const handleCollapseSection = () => {
    updateColapsedSections("measurementSection");
    if (collapsedSection.value === true) {
      setShowNewData(false);
    }
  };

  // Don't render section if no configs available
  if (!configs || configs.length === 0) {
    return null;
  }

  const hasCustomViewType =
    !!portalPreferences?.selectedViewType &&
    portalPreferences.selectedViewType !== DEFAULT_VIEW_TYPE;

  return (
    <>
      {!isMobile && (
        <TitleContainer>
          <PageTitle>{t("patientPage_sections_measurements")}</PageTitle>
          {showNewData && (
            <NewDataCOntainer>
              {t("patientPage_sections_newData")}
            </NewDataCOntainer>
          )}
          <CustomIconButton
            style={{ marginLeft: "2px" }}
            onClick={handleCollapseSection}
          >
            {collapsedSection.value ? <ChevronUp /> : <ChevronDown />}
          </CustomIconButton>
        </TitleContainer>
      )}

      <ChartsContainer
        style={{
          height: collapsedSection.value ? "0" : "",
          overflow: collapsedSection.value ? "hidden" : "",
        }}
      >
        <AddMeasurementButton onClick={handleAddMeasurement}>
          {t("patientPage_measurements_add")}
        </AddMeasurementButton>
        {isMobile && (
          <MobilePatientPageTitleContainer>
            <PageTitle>{t("patientPage_sections_measurements")}</PageTitle>
          </MobilePatientPageTitleContainer>
        )}

        {/* POC: Dual Y-Axis Chart */}
        <DualAxisChartCard
          title="FEV1 (L), cfDNA (%)"
          onClick={() =>
            openModal(<DualAxisChartModal />, {
              width: "auto",
              height: "676px",
              showCloseButton: true,
              padding: "12px 0 0 24px",
            })
          }
        />

        {configs
          ?.filter(
            (measurement) =>
              measurement.type !== portalPreferences.pinnedMeasurementType &&
              !measurement.type.startsWith("z_score") &&
              // EN-5042: hide the Breathlessness (mMRC) graph on UAT only. The
              // Configs API emits this card as "dyspnea" (the measurement type);
              // "breathlessness" is matched too for safety against legacy configs.
              !(
                isUat() &&
                (measurement.type === "dyspnea" ||
                  measurement.type === "breathlessness")
              )
          )
          .sort((a, b) => {
            // EN-4972: when a view type is selected, the API already returns
            // the cards in the configured order — keep it untouched.
            if (hasCustomViewType) {
              return 0;
            }

            const aIsSpirometry = spirometryMeasurementsValues.includes(a.type);
            const bIsSpirometry = spirometryMeasurementsValues.includes(b.type);

            const nebA = a.type === "nebulizer";
            const nebB = b.type === "nebulizer";
            if (nebA && !nebB) return -1;
            if (!nebA && nebB) return 1;

            if (aIsSpirometry && !bIsSpirometry) return -1;
            if (!aIsSpirometry && bIsSpirometry) return 1;
            return 0;
          })
          .map((measurement, index) => {
            // EN-5027: ECG is a bespoke card, not a chart-backed measurement.
            // When the API places it in the view-type order (e.g. cardiac at
            // position 5) it arrives as a config entry — render the dedicated
            // EcgCard inline at that position instead of a MeasurementCard.
            if (measurement.type === "ecg") {
              return <EcgCard key={`ecg_${index}`} patient={patient} />;
            }

            return (
              <MeasurementCard
                key={`${measurement.title}_${measurement.type}_${index}`}
                cardTitle={`${
                  measurement.type === "bp_diastolic" ||
                  measurement.type === "bp_systolic"
                    ? t("patientPage_measurements_bloodPressure")
                    : measurement.title
                } ${measurement.unit ? `(${measurement.unit})` : ""}`}
                unit={measurement.unit}
                measurementType={
                  measurement.type === "breathlessness"
                    ? "dyspnea"
                    : (measurement.type as MeasurementTypes)
                }
                minValue={measurement.min}
                maxValue={measurement.max}
                openFev1Graph={openFev1Graph}
                onFev1GraphOpen={onFev1GraphOpen}
                checkNewData={checkNewData}
                patient={patient}
              />
            );
          })}

        {isEcgEnabled &&
          !configs.some((c) => c.type === "ecg") &&
          portalPreferences.pinnedMeasurementType !== "ecg" && (
            <EcgCard patient={patient} />
          )}
      </ChartsContainer>
    </>
  );
}
